'use client';

import { useState, useEffect } from 'react';
import { Save, Loader2, AlertCircle, HelpCircle, Info } from 'lucide-react';
import { useTranslation } from '@/providers/I18nProvider';
import { useAgent } from '@/hooks/useAgent';
import { useToast } from '@/hooks/useToast';

interface GeneralSettingsProps {
  agentId: string;
  agentName: string;
  initialSettings: {
    welcomeMessage?: string;
    welcomeSubMessage?: string;
    placeholderQuestions?: string[];
    buttonText?: string;
  };
  isClientApi?: boolean;
}

export default function GeneralSettings({ agentId, agentName: initialAgentName, initialSettings, isClientApi = false }: GeneralSettingsProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [agentName, setAgentName] = useState(initialAgentName || '');
  const [welcomeMessage, setWelcomeMessage] = useState(initialSettings.welcomeMessage || '');
  const [welcomeSubMessage, setWelcomeSubMessage] = useState(initialSettings.welcomeSubMessage || '');
  const [buttonText, setButtonText] = useState(initialSettings.buttonText || '');
  const [placeholderQuestions, setPlaceholderQuestions] = useState<string[]>(
    initialSettings.placeholderQuestions || (isClientApi ? ['', '', ''] : ['', '', '', ''])
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Use agent hook for mutation
  const { updateAgent, agent } = useAgent(agentId);
  
  // Update local state when agent data changes (after save)
  useEffect(() => {
    if (agent) {
      setAgentName(agent.name || '');
      if (agent.configDoc) {
        setWelcomeMessage(agent.configDoc.welcomeMessage || '');
        setWelcomeSubMessage(agent.configDoc.welcomeSubMessage || '');
        setButtonText(agent.configDoc.buttonText || '');
        setPlaceholderQuestions(agent.configDoc.placeholderQuestions || (isClientApi ? ['', '', ''] : ['', '', '', '']));
      }
    }
  }, [agent, isClientApi]);

  const handleQuestionChange = (index: number, value: string) => {
    const newQuestions = [...placeholderQuestions];
    newQuestions[index] = value;
    setPlaceholderQuestions(newQuestions);
  };

  const handleSave = async () => {
    setError('');
    setIsSaving(true);

    try {
      const updates: any = {
        name: agentName,
        welcomeMessage: welcomeMessage,
        placeholderQuestions: placeholderQuestions.filter(q => q.trim() !== ''),
      };
      
      if (isClientApi) {
        updates.buttonText = buttonText;
      } else {
        updates.welcomeSubMessage = welcomeSubMessage;
      }
      
      await updateAgent.mutateAsync(updates);

      showToast(t('admin.general.saveSuccess'), 'success');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.errors.saveFailed'));
      showToast(err instanceof Error ? err.message : t('common.errors.saveFailed'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{t('admin.general.title')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('admin.general.description')}
          </p>
        </div>

        {/* Settings sections */}
        <div className="space-y-8">
          {/* Basic Information */}
          <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Info className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.general.sections.basicInfo')}</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('admin.general.agentNameLabel')}
                </label>
                <input
                  type="text"
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  placeholder={t('admin.general.agentNamePlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                           bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                           focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('admin.general.agentNameHelp')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('admin.general.welcomeMessageLabel')}
                </label>
                <input
                  type="text"
                  value={welcomeMessage}
                  onChange={(e) => setWelcomeMessage(e.target.value)}
                  placeholder={t('chat.welcome.defaultTitle', { agentName })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                           bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                           focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('admin.general.welcomeMessageHelp')}
                </p>
              </div>

              {isClientApi ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('admin.general.buttonTextLabel')}
                  </label>
                  <input
                    type="text"
                    value={buttonText}
                    onChange={(e) => setButtonText(e.target.value)}
                    placeholder={t('admin.general.buttonTextPlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                             bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                             focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('admin.general.buttonTextHelp')}
                  </p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('admin.general.subMessageLabel')}
                  </label>
                  <input
                    type="text"
                    value={welcomeSubMessage}
                    onChange={(e) => setWelcomeSubMessage(e.target.value)}
                    placeholder={t('chat.welcome.defaultSubMessage')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                             bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                             focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('admin.general.subMessageHelp')}
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* Placeholder Questions */}
          <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center gap-2 mb-4">
              <HelpCircle className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.general.sections.suggestedQuestions')}</h2>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {isClientApi ? t('admin.general.questionsHelpWidget') : t('admin.general.questionsHelp')}
            </p>
            
            <div className="space-y-3">
              {placeholderQuestions.map((question, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={question}
                    onChange={(e) => handleQuestionChange(index, e.target.value)}
                    placeholder={t('admin.general.questionPlaceholder', { number: index + 1 })}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg
                             bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                             focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                  />
                  {placeholderQuestions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newQuestions = placeholderQuestions.filter((_, i) => i !== index);
                        setPlaceholderQuestions(newQuestions);
                      }}
                      className="px-3 py-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
              {placeholderQuestions.length < 4 && (
                <button
                  type="button"
                  onClick={() => setPlaceholderQuestions([...placeholderQuestions, ''])}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  {t('admin.general.addQuestion')} ({placeholderQuestions.length}/4)
                </button>
              )}
            </div>
          </section>

          {/* Error Messages */}
          {error && (
            <div className="flex items-start gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg
                       transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                       flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {t('admin.general.saveButton')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}